import {
  PuxButton,
  PuxContent,
  PuxDialogTitle,
  PuxSpinner,
  PuxText,
  PuxTypography,
  useTranslation,
} from "@phonero/pux-react"
import React from "react"
import {
  AuthenticationStepUpCache,
  AuthenticationLevel,
  useAuthentication,
  useAuthenticationStepUpCache,
  useTokenData,
  useAppQuery,
} from "../util"
import IconLoginWithBankId from "./IconLoginWithBankId"
import { MobileSubscriptionUsageEnum } from "@phonero/common-graphql/types"
import { SubscriptionUsageTypeDocument } from "../queries/MobileSubscriptionUsageType.generated"
import {
  UsageTypePersonalOnlyInfoBox
} from "../Other/UsageTypePersonalOnlyInfoBox"

interface StepUpAuthenticationProps {
  title: string
  level?: AuthenticationLevel
  children: React.ReactNode
  onCancel?: () => void
  cancelText?: string
  stepUpCache?: () => AuthenticationStepUpCache
}

export const StepUpAuthentication: React.FC<StepUpAuthenticationProps> = ({
  title,
  level = AuthenticationLevel.Enhanced,
  stepUpCache,
  onCancel,
  cancelText = "Avbryt",
  children,
}) => {
  const { t } = useTranslation()
  const { subscriptionId } = useTokenData()
  const { isAuthenticatedAtLevel, loginWithBankId } = useAuthentication(level)
  const {
    isCacheActive,
    pushItem: PushItem,
    clear: Clear,
  } = useAuthenticationStepUpCache()

  const { data, loading } = useAppQuery(SubscriptionUsageTypeDocument, {
    variables: {
      id: subscriptionId ?? "",
    },
    skip: !subscriptionId,
  })

  const mobileSubscription = data?.mobileSubscription
  const usageType = mobileSubscription?.usageType

  if (!!isAuthenticatedAtLevel && isCacheActive) Clear()
  if (!isAuthenticatedAtLevel && stepUpCache) PushItem(stepUpCache())

  if (!!isAuthenticatedAtLevel) return <>{children}</>

  return (
    <>
      <PuxContent>
        <PuxDialogTitle onDismissClick={onCancel}>
          <PuxText style={{ fontSize: "2.6rem", fontWeight: 900 }}>
            {title}
          </PuxText>
        </PuxDialogTitle>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            paddingTop: "2rem",
          }}
        >
          {!!loading && <PuxSpinner />}
          {!loading && usageType === MobileSubscriptionUsageEnum.Impersonal && (
            <>
              <UsageTypePersonalOnlyInfoBox backgroundColor={"light-yellow"} />
              <PuxTypography
                variant="h5"
                component="h5"
                style={{ maxWidth: "600px", textAlign: "center" }}
              >
                Din administrator kan gjøre endingen i DinBedrift eller endre
                abonnementet ditt til et personlig abonnement.
              </PuxTypography>
            </>
          )}
          {!loading && usageType !== MobileSubscriptionUsageEnum.Impersonal && (
            <>
              <PuxTypography
                variant="h3"
                component="h3"
                style={{ maxWidth: "600px", textAlign: "center" }}
              >
                For å gjennomføre denne handlingen må du først bekrefte at du er
                deg med BankID
              </PuxTypography>
              <PuxButton
                fill="clear"
                color="primary"
                size="large"
                onClick={async () => {
                  await loginWithBankId()
                }}
              >
                <IconLoginWithBankId />
              </PuxButton>
            </>
          )}
          {!loading && !!onCancel && (
            <PuxButton color="secondary" fill="clear" onClick={onCancel}>
              {cancelText}
            </PuxButton>
          )}
        </div>
      </PuxContent>
    </>
  )
}
